import AutoLoadingButton from "@/components/AutoLoadingButton";
import ProcessingModal from "@/components/ProcessingModal";
import OrderDTO from "@/dto/order/OrderDTO";
import ShopDTO from "@/dto/shop/ShopDTO";
import { useModalStore } from "@/hooks/Modal/hooks";
import OrderTab from "@/scenes/OrderManagement/enum/OrderTab";
import { DropshipOrderOutboundResult, jdOutBound, JdOutboundDropshipOrder } from "@/services/api/jd/order";
import { message } from "antd";
import { chunk } from "lodash";
import { observer } from "mobx-react-lite";
import React from "react";
import OutBoundResultModal from "./OutBoundResultModal";
import { useOrderManagementStore } from "@/scenes/OrderManagement/store/OrderManagementStore";

interface OutBoundButtonProps {
    shop: ShopDTO;
    getSelectedOrders(): OrderDTO[];
    onDone?: () => void;
    tab: OrderTab;
}

const OutBoundButton: React.FC<OutBoundButtonProps> = function (props) {
    const { getSelectedOrders, shop, tab } = props;

    const modalStore = useModalStore();
    const orderManagementStore = useOrderManagementStore();

    const onDone = async () => {
        orderManagementStore.fetchOrders(shop.id, tab);
        orderManagementStore.getOrderListCount();
    };

    async function executeOutBound(selectedOrders: OrderDTO[]) {
        const result: {
            successCount: number;
            failureCount: number;
            errorResults: DropshipOrderOutboundResult[];
        } = { successCount: 0, failureCount: 0, errorResults: [] };

        let processed = 0;
        const total = selectedOrders.length;
        const loadingModal = modalStore.openModal(ProcessingModal, { current: 0, count: total, text: "正在出库中..." });

        const parts = chunk(selectedOrders, 10);
        for (const part of parts) {
            try {
                loadingModal.update({
                    current: processed,
                    count: total,
                    nextMaxFakeNumber: processed + part.length,
                });

                const orderParams: JdOutboundDropshipOrder[] = selectedOrders.map(({ id, originalId }) => ({
                    id,
                    originalId,
                }));

                const res = await jdOutBound(shop.id, orderParams);

                const successfulResults = [];
                const failResults = [];

                res.forEach((it) => {
                    if (it.success) {
                        successfulResults.push(it);
                    } else {
                        failResults.push(it);
                    }
                });

                result.successCount += successfulResults.length;
                result.failureCount += failResults.length;
                result.errorResults.push(...failResults);
                processed += part.length;

                loadingModal.update({ current: processed, count: total });
            } catch (err: any) {
                part.forEach((it) => {
                    result.errorResults.push({
                        orderId: it.id,
                        originalId: it.originalId,
                        message: err?.message ?? `出库失败,未知错误 ${err?.trace}`,
                        success: false,
                    });
                });
                result.failureCount += part.length;
            }
        }

        loadingModal.onCancel();

        return result;
    }

    async function processOutBoundResult(
        successCount: number,
        failureCount: number,
        errorResults: DropshipOrderOutboundResult[],
    ) {
        const allSucceeded = failureCount === 0;

        if (allSucceeded) {
            message.success("出库成功");
            onDone?.();
            return;
        }

        modalStore.openModal(OutBoundResultModal, {
            successCount,
            failureCount,
            errorResults,
            onCancel() {
                setTimeout(() => onDone?.(), 16);
            },
        });
    }

    async function handleOutBound() {
        const selectedOrders = getSelectedOrders();

        const { successCount, failureCount, errorResults } = await executeOutBound(selectedOrders);

        await processOutBoundResult(successCount, failureCount, errorResults);
    }

    return (
        <AutoLoadingButton type="primary" className="ant-btn-warning mr3" onClick={handleOutBound}>
            出库
        </AutoLoadingButton>
    );
};

export default observer(OutBoundButton);
